import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { Button } from "@/components/ui/button"
import {
  ArrowRight,
  Bot,
  Cog,
  Briefcase,
  TrendingUp,
  Workflow,
} from "lucide-react"

import { Link } from "react-router-dom"

import { SEO } from "@/components/SEO"
import { PageFAQ } from "@/components/PageFAQ"
import { appliedIntelligenceFAQs } from "@/data/faqData"

export default function AppliedIntelligence() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <SEO
        title="Applied Intelligence | Operationalizing AI | RSA Tech Group"
        description="Operationalizing machine learning models to solve complex business challenges at scale. Moving from theory to practice with ROI-focused AI."
        image="https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&q=80&w=2000"
      />
      <Navigation />

      {/* Hero Section */}
      <div className="relative pt-32 pb-24 bg-primary overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.1)_0%,transparent_70%)] opacity-30 pointer-events-none" />
        <div className="section-container relative z-10 text-center">
          <h1 className="text-4xl md:text-6xl font-black text-white tracking-tight mb-8">
            Applied <span className="text-accent">Intelligence</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-10">
            Operationalizing machine learning models to solve complex business
            challenges at scale.
          </p>
        </div>
      </div>

      {/* Content Section */}
      <section className="py-20 bg-gray-50">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
                AI That Works
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Applied Intelligence is about moving from theory to practice. We
                take cutting-edge AI research and apply it to real-world
                business problems - automating workflows, optimizing supply
                chains, and enhancing customer service.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We help you identify the high-impact use cases where AI can
                deliver immediate ROI, and then build the robust, scalable
                solutions to make it happen.
              </p>

              <div className="grid grid-cols-1 gap-4 mb-8">
                {[
                  { icon: Bot, text: "Robotic Process Automation (RPA)" },
                  { icon: TrendingUp, text: "Demand Forecasting" },
                  { icon: Cog, text: "Predictive Maintenance" },
                  { icon: Workflow, text: "Intelligent Document Processing" },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center text-primary font-medium p-4 bg-white rounded-lg shadow-sm border border-gray-100"
                  >
                    <div className="p-2 bg-accent/10 rounded-md mr-4">
                      <item.icon className="h-5 w-5 text-accent" />
                    </div>
                    {item.text}
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-accent/10 rounded-lg transform translate-x-4 translate-y-4" />
              <img
                src="https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&q=80&w=2000"
                alt="AI Automation in Business"
                className="rounded-lg shadow-xl relative z-10 w-full object-cover h-[500px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-white">
        <div className="section-container">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-primary mb-6">
              Solutons at Scale
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Smart Logistics",
                desc: "Optimizing routes and inventory management with predictive AI.",
                img: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d",
              },
              {
                title: "Risk Management",
                desc: "Detecting fraud and compliance risks in real-time with anomaly detection.",
                img: "https://images.unsplash.com/photo-1563986768609-322da13575f3",
              },
              {
                title: "Customer insights",
                desc: "Analyzing customer behavior to drive retention and sales growth.",
                img: "https://images.unsplash.com/photo-1551434678-e076c223a692",
              },
            ].map((card, i) => (
              <div
                key={i}
                className="group rounded-xl overflow-hidden shadow-lg border border-gray-100"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={card.img + "?auto=format&fit=crop&q=80&w=800"}
                    alt={card.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-primary mb-3">
                    {card.title}
                  </h3>
                  <p className="text-gray-600">{card.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-8"
              >
                Deploy Intelligence <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PageFAQ
        faqs={appliedIntelligenceFAQs}
        title="Applied Intelligence FAQs"
        subtitle="Common questions about operationalizing AI and machine learning"
        variant="light"
      />

      <Footer />
    </div>
  )
}
